import * as React from 'react'

import { ApolloDefender } from './ApolloDefender'
import { useErrorHandlerCtx } from './ErrorHandlerProvider'
import { isAuthError } from './helpers/errorTypeHelpers'

type TOnOperationErrorOptions = FirstParam<PropsOf<typeof ApolloDefender>['onOperationError']>

type TProps = RequiredChildren

/**
 * Error handler for server-communication-related errors
 * covers network errors, BE error and user errors returned from BE on mutation / query
 */
export const GraphQLErrorGuard: React.FC<TProps> = ({ children }) => {
  const { setError } = useErrorHandlerCtx()

  // isUnreachable === error msg contains "Unexpected end of" or "Failed to fetch"
  const onNetworkError = React.useCallback(
    (error: Error, isUnreachable: boolean) => {
      if (isAuthError(error)) {
        setError({ type: 'authError', payload: error })
        return
      }

      const errorType = isUnreachable ? 'serviceUnreachable' : 'networkError'
      setError({ type: errorType, payload: error })
    },
    [setError],
  )

  // query, mutation (or subscription?) returned some error from BE that should be presented to the user
  const onUserErrors = React.useCallback(
    (messages: RoA<string>) => {
      setError({ type: 'userErrors', payload: messages })
    },
    [setError],
  )

  // query, mutation (or subscription?) return some errors from BE that the user doesn’t need to see
  const onOperationError = React.useCallback(
    ({ error, sentryEventId }: TOnOperationErrorOptions) => {
      setError({ type: 'operationError', payload: error, sentryEventId })
    },
    [setError],
  )

  return (
    <ApolloDefender
      onNetworkError={onNetworkError}
      onUserErrors={onUserErrors}
      onOperationError={onOperationError}
    >
      <>{children}</>
    </ApolloDefender>
  )
}
